/**
 * AudioProcessorService - Timer + Silence-Based Chunking with AnalyserNode
 *
 * This service implements the exact logic from the Angular version:
 * 1. Record audio continuously into buffer (NO data loss)
 * 2. Every uploadInterval (e.g., 20 seconds), start monitoring for 500ms silence
 * 3. When 500ms silence detected, chunk from last chunk end to silence
 * 4. Upload chunk and continue recording
 * 5. Repeat - next chunk starts exactly where last chunk ended
 */

export interface AudioChunkEvent {
  blob: Blob;
  timestamp: number;
}

export interface AudioProcessorConfig {
  bufferSize: number;
  uploadInterval: number; // in seconds
  silenceThreshold?: number;
  silenceDurationThreshold?: number; // in milliseconds
}

export class AudioProcessorService {
  private stream: MediaStream | null = null;
  private isProcessing: boolean = false;
  private isPaused: boolean = false;
  private readonly DEBUG = false;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private dataArray: Uint8Array | null = null;
  private bufferSize: number = 4096;
  private audioBuffer: Float32Array[] = [];
  private sampleRate: number = 44100;
  private scriptProcessor: ScriptProcessorNode | null = null;

  // Simple chunking properties
  private silenceThreshold: number = 0.01;
  private lastChunkEndIndex: number = 0; // Where last chunk ended

  // Audio level detection
  private audioLevelAnimationFrame: number | null = null;
  private timeDomainArray: Float32Array | null = null;

  // Frequency data for waveform (matching Angular)
  private frequencyDataArray: Uint8Array | null = null;
  private frequencyAnimationFrame: number | null = null;

  // Event emitter for chunk ready events
  private listeners: ((event: AudioChunkEvent) => void)[] = [];

  // Event emitter for audio level changes (for waveform)
  private audioLevelListeners: ((level: number) => void)[] = [];

  // Event emitter for frequency data changes (matching Angular's waveformData$)
  private frequencyDataListeners: ((data: Uint8Array) => void)[] = [];

  /**
   * Add event listener for audio chunk ready events
   */
  onAudioChunkReady(callback: (event: AudioChunkEvent) => void): () => void {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Add event listener for audio level changes (for waveform visualization)
   */
  onAudioLevelChange(callback: (level: number) => void): () => void {
    this.audioLevelListeners.push(callback);
    return () => {
      const index = this.audioLevelListeners.indexOf(callback);
      if (index > -1) {
        this.audioLevelListeners.splice(index, 1);
      }
    };
  }

  /**
   * Add event listener for frequency data changes (matching Angular's waveformData$)
   */
  onFrequencyDataChange(callback: (data: Uint8Array) => void): () => void {
    this.frequencyDataListeners.push(callback);
    return () => {
      const index = this.frequencyDataListeners.indexOf(callback);
      if (index > -1) {
        this.frequencyDataListeners.splice(index, 1);
      }
    };
  }

  /**
   * Emit audio chunk ready event
   */
  private emitAudioChunkReady(event: AudioChunkEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  /**
   * Emit audio level change event
   */
  private emitAudioLevelChange(level: number): void {
    this.audioLevelListeners.forEach((listener) => listener(level));
  }

  /**
   * Emit frequency data change event
   */
  private emitFrequencyDataChange(data: Uint8Array): void {
    this.frequencyDataListeners.forEach((listener) => listener(data));
  }

  /**
   * Initialize audio processing with AnalyserNode
   */
  async initialize(
    stream: MediaStream,
    config: AudioProcessorConfig
  ): Promise<void> {
    try {
      this.stream = stream;

      // Create audio context
      this.audioContext = new AudioContext();

      this.bufferSize = config.bufferSize;
      this.sampleRate = this.audioContext.sampleRate;
      this.silenceThreshold = config.silenceThreshold || 0.01;

      // Create media stream source
      this.source = this.audioContext.createMediaStreamSource(stream);

      // Create analyser (matching Angular's setup)
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256; // Match Angular's fftSize
      this.analyser.smoothingTimeConstant = 0.8;
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.frequencyDataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.timeDomainArray = new Float32Array(this.analyser.frequencyBinCount);

      // Connect the audio nodes (don't connect to destination to avoid playback)
      this.source.connect(this.analyser);

      // Set up audio processing for recording
      this.setupAudioProcessing();
    } catch (error) {
      console.error('[AudioProcessor] Initialization failed:', error);
      throw new Error(
        `Audio processor initialization failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Set up audio processing for recording and level detection
   */
  private setupAudioProcessing(): void {
    if (!this.audioContext || !this.source) return;

    // Use ScriptProcessorNode for reliable background processing
    this.scriptProcessor = this.audioContext.createScriptProcessor(
      this.bufferSize,
      1,
      1
    );
    this.scriptProcessor.onaudioprocess = (event) => {
      if (!this.isProcessing || this.isPaused) return;

      const inputData = event.inputBuffer.getChannelData(0);
      const audioData = new Float32Array(inputData);
      this.audioBuffer.push(audioData);

      if (this.audioBuffer.length % 100 === 0) {
        console.log(
          `[AudioProcessor] Audio buffer: ${this.audioBuffer.length} buffers`
        );
      }
    };

    // Connect the source to the script processor
    this.source.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioContext.destination); // Required for script processor to work
  }

  /**
   * Start audio level detection
   */
  private startAudioLevelDetection(): void {
    if (!this.analyser || !this.timeDomainArray) return;

    const detectAudioLevel = () => {
      if (!this.isProcessing || this.isPaused) {
        this.audioLevelAnimationFrame = null;
        return;
      }

      this.analyser!.getFloatTimeDomainData(this.timeDomainArray!);

      // Calculate RMS (Root Mean Square) and peak for audio level
      let sum = 0;
      let max = 0;
      let count = 0;

      for (let i = 0; i < this.timeDomainArray!.length; i++) {
        const sample = Math.abs(this.timeDomainArray![i]);
        max = Math.max(max, sample);
        sum += sample * sample;
        count++;
      }

      const rms = Math.sqrt(sum / count);

      // Enhanced audio level calculation
      let level = 0;

      // Use multiple detection methods for better sensitivity
      const peakLevel = max;
      const rmsLevel = rms;

      // Very sensitive threshold - detect even quiet sounds
      if (rmsLevel > 0.0001 || peakLevel > 0.001) {
        // Calculate dB level with adjusted reference
        // Using -80 dB as floor for very quiet sounds
        const rmsDb = 20 * Math.log10(Math.max(0.0001, rmsLevel));
        const peakDb = 20 * Math.log10(Math.max(0.001, peakLevel));

        // Normalize dB to 0-1 range
        // -80 dB = silence, -40 dB = quiet speech, -20 dB = normal speech, 0 dB = loud
        const rmsNormalized = Math.max(0, Math.min(1, (rmsDb + 80) / 80));
        const peakNormalized = Math.max(0, Math.min(1, (peakDb + 60) / 60));

        // Combine RMS and peak with emphasis on RMS for stability
        const combinedLevel = rmsNormalized * 0.8 + peakNormalized * 0.2;

        // Apply expansion curve to make levels more visible
        // This makes quiet sounds more apparent while keeping loud sounds in range
        if (combinedLevel < 0.1) {
          // Boost very quiet sounds
          level = Math.pow(combinedLevel * 3, 0.7);
        } else if (combinedLevel < 0.3) {
          // Moderate boost for quiet speech
          level = Math.pow(combinedLevel * 1.5, 0.8);
        } else {
          // Normal scaling for regular speech
          level = Math.pow(combinedLevel, 0.9);
        }

        // Ensure minimum visible level for any detected sound
        if (level < 0.02 && (rmsLevel > 0.0005 || peakLevel > 0.005)) {
          level = 0.02 + rmsLevel * 50;
        }

        // Clamp to valid range
        level = Math.max(0, Math.min(1, level));
      } else {
        // Complete silence
        level = 0;
      }

      // Emit audio level for waveform
      this.emitAudioLevelChange(level);

      // Continue detection
      this.audioLevelAnimationFrame = requestAnimationFrame(detectAudioLevel);
    };

    this.audioLevelAnimationFrame = requestAnimationFrame(detectAudioLevel);
  }

  /**
   * Start frequency data detection (matching Angular's animateWaveform)
   */
  private startFrequencyDataDetection(): void {
    if (!this.analyser || !this.frequencyDataArray) return;

    const detectFrequencyData = () => {
      if (!this.isProcessing) {
        this.frequencyAnimationFrame = null;
        return;
      }

      if (this.isPaused) {
        // Emit flatline when paused (matching Angular)
        const flatline = new Uint8Array(this.analyser!.frequencyBinCount);
        flatline.fill(128); // middle of 0–255 to draw flatline
        this.emitFrequencyDataChange(flatline);
      } else {
        // Get frequency data (matching Angular's getByteFrequencyData)
        this.analyser!.getByteFrequencyData(this.frequencyDataArray!);

        // Emit frequency data for waveform
        this.emitFrequencyDataChange(this.frequencyDataArray!);
      }

      // Continue detection
      this.frequencyAnimationFrame = requestAnimationFrame(detectFrequencyData);
    };

    this.frequencyAnimationFrame = requestAnimationFrame(detectFrequencyData);
  }

  /**
   * Stop audio level detection
   */
  private stopAudioLevelDetection(): void {
    if (this.audioLevelAnimationFrame !== null) {
      cancelAnimationFrame(this.audioLevelAnimationFrame);
      this.audioLevelAnimationFrame = null;
    }
  }

  /**
   * Stop frequency data detection
   */
  private stopFrequencyDataDetection(): void {
    if (this.frequencyAnimationFrame !== null) {
      cancelAnimationFrame(this.frequencyAnimationFrame);
      this.frequencyAnimationFrame = null;
    }
  }

  /**
   * Start processing audio
   */
  async startProcessing(): Promise<void> {
    try {
      if (this.isProcessing) {
        return;
      }

      if (!this.audioContext || !this.analyser || !this.source) {
        throw new Error('Audio processor not properly initialized');
      }

      this.isProcessing = true;
      this.isPaused = false;
      this.audioBuffer = [];
      this.lastChunkEndIndex = 0;

      // Resume audio context if suspended (required for modern browsers)
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (error) {
          console.error(
            '[AudioProcessor] Failed to resume audio context:',
            error
          );
          throw new Error('Failed to resume audio context');
        }
      }

      // Start audio level detection
      this.startAudioLevelDetection();

      // Start frequency data detection (matching Angular)
      this.startFrequencyDataDetection();
    } catch (error) {
      console.error('[AudioProcessor] Failed to start processing:', error);
      this.isProcessing = false;
      throw error;
    }
  }

  /**
   * Wait for a period of silence.
   */
  async waitForSilence(silenceDurationThreshold: number): Promise<void> {
    return new Promise((resolve) => {
      let silenceStartTime = 0;
      let isInSilence = false;

      const monitor = () => {
        if (!this.isProcessing || this.isPaused) {
          resolve();
          return;
        }

        const currentLevel = this.getCurrentAudioLevel();
        if (currentLevel <= this.silenceThreshold) {
          if (!isInSilence) {
            isInSilence = true;
            silenceStartTime = Date.now();
          } else {
            if (Date.now() - silenceStartTime >= silenceDurationThreshold) {
              resolve();
              return;
            }
          }
        } else {
          isInSilence = false;
        }
        requestAnimationFrame(monitor);
      };
      monitor();
    });
  }

  /**
   * Get current audio level from the analyser
   */
  private getCurrentAudioLevel(): number {
    if (!this.analyser || !this.timeDomainArray) {
      return 0;
    }

    this.analyser.getFloatTimeDomainData(this.timeDomainArray);

    // Calculate RMS (Root Mean Square) for audio level
    let sum = 0;
    let count = 0;

    for (let i = 0; i < this.timeDomainArray.length; i++) {
      const sample = Math.abs(this.timeDomainArray[i]);
      sum += sample * sample;
      count++;
    }

    const rms = Math.sqrt(sum / count);
    return rms;
  }

  /**
   * Create chunk from accumulated audio data
   */
  async createChunk(): Promise<{ blob: Blob; durationSeconds: number }> {
    const startIndex = this.lastChunkEndIndex;
    const endIndex = this.audioBuffer.length;

    if (startIndex >= endIndex) {
      return { blob: new Blob([], { type: 'audio/wav' }), durationSeconds: 0 };
    }

    const chunkBuffers = this.audioBuffer.slice(startIndex, endIndex);
    const estimatedDuration = this.estimateDuration(chunkBuffers);

    try {
      const audioBlob = await this.convertToWav(chunkBuffers);

      // Update last chunk end index
      this.lastChunkEndIndex = endIndex;

      console.log(
        `[AudioProcessor] ✅ Chunk created successfully: ${
          audioBlob.size
        } bytes, duration: ${estimatedDuration.toFixed(
          3
        )}s, emitting for upload...`
      );
      this.emitAudioChunkReady({
        blob: audioBlob,
        timestamp: Date.now(),
      });
      return { blob: audioBlob, durationSeconds: estimatedDuration };
    } catch (error) {
      console.error('[AudioProcessor] ❌ Error creating chunk:', error);
      throw error;
    }
  }

  /**
   * Stop processing and get final chunk
   */
  async stopProcessing(): Promise<{ blob: Blob; durationSeconds: number }> {
    this.isProcessing = false;

    // Stop audio level detection
    this.stopAudioLevelDetection();

    // Stop frequency data detection
    this.stopFrequencyDataDetection();

    // Create final chunk with any remaining audio
    if (this.audioBuffer.length > this.lastChunkEndIndex) {
      return this.createFinalChunk();
    } else {
      return { blob: new Blob([], { type: 'audio/wav' }), durationSeconds: 0 };
    }
  }

  /**
   * Create final chunk with remaining audio
   */
  private async createFinalChunk(): Promise<{
    blob: Blob;
    durationSeconds: number;
  }> {
    const startIndex = this.lastChunkEndIndex;
    const endIndex = this.audioBuffer.length;
    const chunkBuffers = this.audioBuffer.slice(startIndex, endIndex);
    const estimatedDuration = this.estimateDuration(chunkBuffers);

    const audioBlob = await this.convertToWav(chunkBuffers);
    return { blob: audioBlob, durationSeconds: estimatedDuration };
  }

  /**
   * Get current buffer with duration information
   */
  async getCurrentBuffer(): Promise<{ blob: Blob; durationSeconds: number }> {
    if (this.audioBuffer.length === 0) {
      return { blob: new Blob([], { type: 'audio/wav' }), durationSeconds: 0 };
    }

    const startIndex = this.lastChunkEndIndex;
    const endIndex = this.audioBuffer.length;

    if (startIndex >= endIndex) {
      return { blob: new Blob([], { type: 'audio/wav' }), durationSeconds: 0 };
    }

    const chunkBuffers = this.audioBuffer.slice(startIndex, endIndex);
    const estimatedDuration = this.estimateDuration(chunkBuffers);
    const audioBlob = await this.convertToWav(chunkBuffers);

    return { blob: audioBlob, durationSeconds: estimatedDuration };
  }

  /**
   * Get complete recording
   */
  async getCompleteRecording(): Promise<Blob> {
    if (this.audioBuffer.length === 0) {
      throw new Error('No audio data available');
    }

    return this.convertToWav(this.audioBuffer);
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.isProcessing = false;
    this.isPaused = false;

    // Stop audio level detection
    this.stopAudioLevelDetection();

    // Stop frequency data detection
    this.stopFrequencyDataDetection();

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.audioBuffer = [];
    this.dataArray = null;
    this.timeDomainArray = null;
    this.frequencyDataArray = null;
  }

  /**
   * Pause processing
   */
  pauseProcessing(): void {
    if (!this.isProcessing) return;

    this.isPaused = true;
    this.stopAudioLevelDetection();

    // Stop frequency detection to prevent animation frame from running
    this.stopFrequencyDataDetection();
  }

  /**
   * Resume processing
   */
  resumeProcessing(): void {
    if (!this.isProcessing || !this.isPaused) return;

    this.isPaused = false;
    this.startAudioLevelDetection();

    // Restart frequency detection
    this.startFrequencyDataDetection();
  }

  /**
   * Check if audio processing is active and receiving data
   * @returns boolean True if processing is active and receiving audio data
   */
  isProcessingAudio(): boolean {
    return this.isProcessing && !this.isPaused && this.audioBuffer.length > 0;
  }

  /**
   * Get the current audio buffer length (for debugging)
   * @returns number The number of audio buffers collected
   */
  getAudioBufferLength(): number {
    return this.audioBuffer.length;
  }

  /**
   * Check if the audio processor is properly initialized
   * @returns boolean True if all required components are initialized
   */
  isInitialized(): boolean {
    return !!(
      this.audioContext &&
      this.analyser &&
      this.source &&
      this.timeDomainArray &&
      this.dataArray
    );
  }

  /**
   * Check if the audio processor is ready for audio level subscriptions
   * @returns boolean True if initialized and ready for audio level detection
   */
  isReadyForAudioLevels(): boolean {
    return this.isInitialized() && this.isProcessing && !this.isPaused;
  }

  /**
   * Convert audio buffers to WAV format
   */
  private async convertToWav(audioBuffer: Float32Array[]): Promise<Blob> {
    const totalLength = audioBuffer.reduce(
      (acc, buffer) => acc + buffer.length,
      0
    );
    const combinedBuffer = new Float32Array(totalLength);

    let offset = 0;
    for (const buffer of audioBuffer) {
      combinedBuffer.set(buffer, offset);
      offset += buffer.length;
    }

    const wavBuffer = this.floatTo16BitPCM(combinedBuffer);
    return this.createWavFile(wavBuffer, this.sampleRate);
  }

  /**
   * Convert float32 audio data to 16-bit PCM
   */
  private floatTo16BitPCM(input: Float32Array): ArrayBuffer {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output.buffer;
  }

  /**
   * Create WAV file
   */
  private createWavFile(samples: ArrayBuffer, sampleRate: number): Blob {
    const buffer = new ArrayBuffer(44 + samples.byteLength);
    const view = new DataView(buffer);

    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.byteLength, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, samples.byteLength, true);

    const pcmData = new Uint8Array(samples);
    const wavData = new Uint8Array(buffer);
    wavData.set(pcmData, 44);

    return new Blob([wavData], { type: 'audio/wav' });
  }

  /**
   * Write string to DataView
   */
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Estimate duration of audio buffers
   */
  private estimateDuration(chunkBuffers: Float32Array[]): number {
    const totalSamples = chunkBuffers.reduce(
      (acc, buffer) => acc + buffer.length,
      0
    );
    return totalSamples / this.sampleRate;
  }
}
